import React, { useEffect, useRef, useState } from 'react';
import { LABEL_CLASS } from 'components/crm/ui/fieldStyles';
import { Plus, X } from 'lucide-react';
import Switch from 'components/crm/ui/Switch';
import useText from 'hooks/useText';

// The api's limits (contract § A): 5 reminders, 4 weeks before at most.
export const MAX_REMINDERS = 5;
export const MAX_REMINDER_MINUTES = 40320;
const METHODS = ['popup', 'email'];
const UNITS = [
  ['weeks', 10080],
  ['days', 1440],
  ['hours', 60],
  ['minutes', 1],
];
const FACTOR = Object.fromEntries(UNITS);
const NEW_REMINDER = { amount: '30', unit: 'minutes', method: 'popup' };

// The largest unit that divides the minutes: 60 reads « 1 heure ».
const toRow = ({ method, minutes }) => {
  const [unit, factor] = UNITS.find(
    ([, size]) => minutes > 0 && minutes % size === 0,
  ) ?? ['minutes', 1];
  return { amount: String(minutes / factor), unit, method };
};

const minutesOf = (row) =>
  Math.round(Number(row.amount || 0) * FACTOR[row.unit]);

const toOverrides = (rows) =>
  rows.map((row) => ({ method: row.method, minutes: minutesOf(row) }));

const sameOverrides = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const selectClass =
  'rounded-[0.4rem] border border-lineStrong bg-white px-2 py-2.5 text-sm font-normal text-gray-900 outline-none focus:border-primary';

/**
 * The reminders of an appointment, as Google Calendar keeps them: `value` is
 * { useDefault, overrides: [{ method: 'popup' | 'email', minutes }] }. Off
 * « Par défaut », up to 5 rows of a number and a unit, sent in minutes.
 */
const RemindersField = ({ id = 'appointment-reminders', value, onChange }) => {
  const { text, plural } = useText('appointmentFields');
  const useDefault = value?.useDefault !== false;
  const overrides = value?.overrides ?? [];
  // The rows keep the unit typed: 60 minutes typed stays in minutes.
  const [rows, setRows] = useState(() => overrides.map(toRow));
  const sent = useRef(overrides);

  // A new value from the parent (another appointment) resets the rows.
  const overridesKey = JSON.stringify(overrides);
  useEffect(() => {
    const next = JSON.parse(overridesKey);
    if (!sameOverrides(next, sent.current)) {
      sent.current = next;
      setRows(next.map(toRow));
    }
  }, [overridesKey]);

  const emit = (nextRows, nextUseDefault = false) => {
    const nextOverrides = nextUseDefault ? [] : toOverrides(nextRows);
    sent.current = nextOverrides;
    setRows(nextUseDefault ? [] : nextRows);
    onChange({ useDefault: nextUseDefault, overrides: nextOverrides });
  };

  const update = (index, patch) =>
    emit(
      rows.map((row, other) => (other === index ? { ...row, ...patch } : row)),
    );

  return (
    <div
      role="group"
      aria-labelledby={`${id}-label`}
      className="flex flex-col gap-3"
    >
      <span
        id={`${id}-label`}
        className={LABEL_CLASS}
      >
        {text('remindersLabel')}
      </span>
      <Switch
        checked={useDefault}
        label={text('remindersDefault')}
        onChange={(checked) =>
          emit(checked ? [] : rows.length ? rows : [NEW_REMINDER], checked)
        }
      />
      {!useDefault && (
        <ul id={id} className="flex flex-col gap-2">
          {rows.map((row, index) => {
            const tooFar = minutesOf(row) > MAX_REMINDER_MINUTES;
            return (
              <li key={index} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    aria-label={text('reminderMethod')}
                    value={row.method}
                    onChange={(event) =>
                      update(index, { method: event.target.value })
                    }
                    className={selectClass}
                  >
                    {METHODS.map((method) => (
                      <option key={method} value={method}>
                        {text(`reminderMethod_${method}`)}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    aria-label={text('reminderAmount')}
                    aria-invalid={tooFar}
                    value={row.amount}
                    onChange={(event) =>
                      update(index, {
                        amount: event.target.value.replace(/[^\d]/g, ''),
                      })
                    }
                    className={`w-20 ${selectClass} ${tooFar ? 'border-danger' : ''}`}
                  />
                  <select
                    aria-label={text('reminderUnit')}
                    value={row.unit}
                    onChange={(event) =>
                      update(index, { unit: event.target.value })
                    }
                    className={selectClass}
                  >
                    {UNITS.map(([unit]) => (
                      <option key={unit} value={unit}>
                        {plural(
                          `reminderUnit_${unit}`,
                          Number(row.amount || 0),
                        )}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-muted">
                    {text('reminderBefore')}
                  </span>
                  <button
                    type="button"
                    aria-label={text('reminderRemove')}
                    onClick={() =>
                      emit(rows.filter((_, other) => other !== index))
                    }
                    className="rounded-lg p-2 text-muted hover:bg-surfaceMuted hover:text-ink"
                  >
                    <X size={16} strokeWidth={2} />
                  </button>
                </div>
                {tooFar && (
                  <p className="text-xs text-danger">
                    {text('reminderTooFar')}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!useDefault && rows.length < MAX_REMINDERS && (
        <button
          type="button"
          onClick={() => emit([...rows, NEW_REMINDER])}
          className="inline-flex w-fit items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-primary hover:bg-surfaceMuted"
        >
          <Plus size={16} strokeWidth={2} />
          {text('reminderAdd')}
        </button>
      )}
    </div>
  );
};

export default RemindersField;
